import { useRef } from 'react'
import MemoryUsedNote from '../shared/MemoryUsedNote'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import CycleWeeks from './CycleWeeks'
import CycleReportPdfButton from './CycleReportPdfButton'
import { errorCode, formatDate } from './cycleStatus'
import { fmtNum, humanizeCode, reasonSentence, valueWithUnit } from './healthReportFormat'
import TargetOverrideNote from './TargetOverrideNote'
import { textsByCode } from './nutritionReportFormat'

const TARGET_KEYS = ['calories_kcal', 'protein_g', 'carbs_rest_day_g', 'carbs_training_day_g', 'fat_g', 'fiber_g', 'target_weight_kg']


function Section({ id, children }) {
  const { t } = useTranslation()
  return (
    <article className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`nutrition-section-${id}`}>
      <h4 className="text-base font-semibold text-gray-900">{t(`cycleReports.nutrition.sections.${id}`)}</h4>
      {children}
    </article>
  )
}

function Condition({ condition }) {
  const { t } = useTranslation()
  return (
    <li className="text-xs text-amber-700">
      {t(`cycleReports.condition.${condition.code}`, {
        defaultValue: humanizeCode(condition.code), marker: condition.marker ?? '', value: condition.value ?? '',
        unlogged: condition.unlogged_kcal_per_day ?? '',
        test: condition.resolving_test ? t(`cycleReports.resolvingTest.${condition.resolving_test}`, humanizeCode(condition.resolving_test)) : '',
      })}
    </li>
  )
}

/** The reason as a sentence built from its parameters; else the AI text; else the short label. */
function useTargetReason(target, aiReason) {
  const { t, i18n } = useTranslation()
  const sentence = reasonSentence(target, i18n.language)
  const text = sentence ? t(`cycleReports.reasonSentence.${sentence.key}`, { ...sentence.params, defaultValue: '' }) : ''
  return text || aiReason || t(`cycleReports.reasonCode.${target.reason_code}`, '')
}

export function TargetRow({ label, target, reason }) {
  const { t, i18n } = useTranslation()
  const reasonText = useTargetReason(target, reason)
  return (
    <li className="border-t border-gray-100 pt-2 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap justify-between gap-2 text-sm">
        <span className="font-medium text-gray-900">{label}</span>
        <span className="text-gray-800">
          {valueWithUnit(target.value, target.unit, i18n.language) || '—'}
          {target.per_kg ? ` (${fmtNum(target.per_kg, i18n.language, 2)} g/kg)` : ''}
        </span>
      </div>
      <TargetOverrideNote target={target} />
      {reasonText && <p className="text-xs text-gray-600">{reasonText}</p>}
      {target.linked_marker && (
        <p className="text-xs text-gray-500">
          {t('cycleReports.nutrition.linkedMarker', { name: target.linked_marker.name || humanizeCode(target.linked_marker.code), value: valueWithUnit(target.linked_marker.value, target.linked_marker.unit, i18n.language) })}
        </p>
      )}
      {target.conditions?.length > 0 && <ul>{target.conditions.map((c, i) => <Condition key={i} condition={c} />)}</ul>}
    </li>
  )
}

// VH-229: the nutrition report of one version — verdict, findings (logging gap first),
// the user's ideas checked, targets with reasons and conditions, strengths, weekly menus.
export function NutritionReportTab({ cycleId, versionNumber, hasBaseline }) {
  const { t, i18n } = useTranslation()
  const printRef = useRef(null)
  const reportQuery = useQuery({
    queryKey: ['healthCycleNutritionReport', cycleId, versionNumber],
    queryFn: () => healthCyclesAPI.getNutritionReport(cycleId, versionNumber),
    select: (r) => r.data,
    enabled: !!cycleId && !!versionNumber,
    retry: (count, err) => err?.response?.status !== 404 && count < 2,
  })

  if (reportQuery.isLoading) return <div className="h-24 rounded-lg border border-gray-200 animate-pulse" />
  if (reportQuery.isError) {
    return (
      <p className="text-sm text-gray-600 py-6 text-center">
        {t(`cycleReports.errors.${errorCode(reportQuery.error)}`, t('healthCycle.errors.generic'))}
      </p>
    )
  }
  const data = reportQuery.data
  const report = data.report
  const targets = data.nutrition_targets
  const texts = report?.texts ?? {}
  // Template texts restate the structured facts word for word; only AI prose may add to them.
  const aiTexts = report?.narrative_source === 'ai'
  const findingTexts = aiTexts ? textsByCode(texts.findings) : {}
  const reasonTexts = aiTexts ? textsByCode(texts.target_reasons) : {}
  const lang = i18n.language
  const ideaTexts = Array.isArray(texts.user_ideas) ? texts.user_ideas : []
  const stamp = t('cycleReports.pdf.stamp', { number: data.version_number, date: t(`healthCycle.status.${data.version_kind === 'draft' ? 'draft' : 'finalized'}`) })
  const canExport = data.version_kind !== 'draft'

  if (!report && !targets) {
    return <p className="text-sm text-gray-600 py-6 text-center" data-testid="nutrition-report-unavailable">{t('cycleReports.errors.nutrition_report_not_available')}</p>
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          {stamp}{data.is_baseline && ` · ${t('cycleReports.nutrition.baseline')}`}
        </p>
        {canExport && <CycleReportPdfButton targetRef={printRef} stamp={stamp} fileName={`nutrition-cycle-${cycleId}-v${data.version_number}.pdf`} />}
      </div>
      <div ref={printRef} className="space-y-3 bg-surface">
        <h3 className="text-lg font-semibold text-gray-900">{t('cycleReports.nutrition.title')}</h3>

        {report && (
          <Section id="verdict">
            <p className="text-sm font-medium text-gray-900">{t(`cycleReports.nutritionVerdict.${report.verdict?.code}`, humanizeCode(report.verdict?.code))}</p>
            {typeof texts.verdict === 'string' && texts.verdict.trim() && <p className="text-sm text-gray-700">{texts.verdict}</p>}
            <MemoryUsedNote memories={report.memories_used} />
          </Section>
        )}

        {report && (
          <Section id="findings">
            {(report.findings ?? []).length === 0 && <p className="text-sm text-gray-500">{t('cycleReports.nutrition.noFindings')}</p>}
            <ul className="space-y-2">
              {(report.findings ?? []).map((f) => (
                <li key={f.code} className="text-sm flex flex-wrap items-baseline gap-x-2" data-testid={`finding-${f.code}`}>
                  <span className="font-medium text-gray-900">{t(`cycleReports.findingCode.${f.code}`, humanizeCode(f.code))}</span>
                  <span className="text-gray-800">{valueWithUnit(f.value, f.unit, lang) || '—'}</span>
                  {f.period_start && (
                    <span className="text-xs text-gray-500">{formatDate(f.period_start, lang)} – {formatDate(f.period_end, lang)}</span>
                  )}
                  {f.linked_marker && (
                    <span className="text-xs text-gray-500">
                      · {t('cycleReports.nutrition.linkedMarker', { name: f.linked_marker.name || humanizeCode(f.linked_marker.code), value: valueWithUnit(f.linked_marker.value, f.linked_marker.unit, lang) })}
                    </span>
                  )}
                  {findingTexts[f.code] && !String(findingTexts[f.code]).includes(String(f.value)) && (
                    <span className="basis-full text-gray-700">{findingTexts[f.code]}</span>
                  )}
                </li>
              ))}
            </ul>
            {(report.omitted ?? []).map((o) => (
              <p key={o.section} className="text-xs text-gray-600">
                {t('cycleReports.nutrition.omitted', { section: t(`cycleReports.nutrition.omittedSection.${o.section}`, humanizeCode(o.section)), reason: t(`cycleReports.omittedReason.${o.reason}`, humanizeCode(o.reason)) })}
              </p>
            ))}
          </Section>
        )}

        {report && (report.user_ideas ?? []).length > 0 && (
          <Section id="user_ideas">
            <ul className="space-y-2">
              {report.user_ideas.map((idea, index) => {
                const checked = ideaTexts.find((x) => x.index === index) ?? ideaTexts[index] ?? {}
                return (
                  <li key={index} className="text-sm">
                    <span className="font-medium text-gray-900">{idea.idea}</span>
                    <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs">{t(`cycleReports.ideaVerdict.${checked.verdict ?? 'not_evaluated'}`, checked.verdict ?? '')}</span>
                    {checked.text && <span className="block text-gray-700">{checked.text}</span>}
                  </li>
                )
              })}
            </ul>
          </Section>
        )}

        <Section id="targets">
          {!targets ? (
            <p className="text-sm text-gray-500">{t('cycleReports.nutrition.noTargets')}</p>
          ) : (
            <>
              {data.is_baseline && <p className="text-xs text-purple-700">{t('cycleReports.nutrition.frozenBaseline')}</p>}
              <ul className="space-y-2">
                {TARGET_KEYS.filter((k) => targets[k]).map((k) => (
                  <TargetRow key={k} label={t(`cycleReports.targetKey.${k}`)} target={targets[k]} reason={reasonTexts[k]} />
                ))}
              </ul>
              {(targets.item_limits ?? []).length > 0 && (
                <>
                  <h5 className="text-xs font-semibold uppercase text-gray-600 pt-2">{t('cycleReports.nutrition.itemLimits')}</h5>
                  <ul className="space-y-2">
                    {targets.item_limits.map((limit) => (
                      <TargetRow key={limit.code} label={t(`cycleReports.limitCode.${limit.code}`, humanizeCode(limit.code))}
                        target={{ ...limit, value: limit.value }} reason={reasonTexts[limit.code]} />
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </Section>

        {report && (report.strengths ?? []).length > 0 && (
          <Section id="strengths">
            <ul className="space-y-1 text-sm text-gray-700">
              {report.strengths.map((s) => (
                <li key={s.code}>
                  <span className="font-medium text-gray-900">{t(`cycleReports.strengthCode.${s.code}`, humanizeCode(s.code))}</span>
                  {valueWithUnit(s.value, s.unit, lang) && `: ${valueWithUnit(s.value, s.unit, lang)}`}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      <Section id="weekly_menus">
        <CycleWeeks cycleId={cycleId} hasBaseline={hasBaseline} />
      </Section>
    </div>
  )
}

export default NutritionReportTab
